import { useEffect, useState } from "react";
import { useSDK } from "../lib/sdk-context";
import { api } from "../lib/api";

interface Chain {
  chain_id: string;
  display_name: string;
  canonical_root: string;
  member_count: number;
}

/**
 * Typeahead picker over core.retail_chain (status='active'), plus an inline
 * "Create new chain" form. On select/create it calls onLink(chain_id) — the
 * parent owns the actual link mutation and toast/refresh.
 */
export default function ChainPicker({ onLink }: { onLink: (chainId: string, displayName: string) => Promise<void> }) {
  const { context } = useSDK();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState<Chain[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get(context, `/api/admin/chains?q=${encodeURIComponent(q)}&status=active`);
        setRows(r.rows || []);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q, context]);

  const select = async (chainId: string, displayName: string) => {
    setBusy(true);
    setError(null);
    try {
      await onLink(chainId, displayName);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const createAndLink = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post(context, "/api/admin/chains", { display_name: name });
      const chain = r.chain as Chain;
      await onLink(chain.chain_id, chain.display_name);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-slate-200 rounded p-3 bg-slate-50">
      {!creating ? (
        <>
          <input
            autoFocus
            className="w-full border border-slate-300 rounded px-2 py-1 bg-white"
            placeholder="Search chains by name…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            disabled={busy}
          />
          <div className="mt-2 text-xs text-slate-500">{loading ? "Searching…" : `${rows.length} chain(s)`}</div>
          <ul className="mt-2 divide-y divide-slate-200 bg-white rounded max-h-64 overflow-auto">
            {rows.map((c) => (
              <li key={c.chain_id} className="px-3 py-2 flex items-center justify-between">
                <div className="text-sm">
                  <div className="font-medium">{c.display_name}</div>
                  <div className="text-xs text-slate-500">
                    {c.member_count} {c.member_count === 1 ? "store" : "stores"}
                  </div>
                </div>
                <button className="btn-primary" disabled={busy} onClick={() => select(c.chain_id, c.display_name)}>
                  Link
                </button>
              </li>
            ))}
          </ul>
          <button
            type="button"
            className="mt-2 text-sm text-accent-700 hover:underline"
            disabled={busy}
            onClick={() => {
              setCreating(true);
              setNewName(q);
            }}
          >
            + Create new chain…
          </button>
        </>
      ) : (
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">New chain name</label>
          <input
            autoFocus
            className="w-full border border-slate-300 rounded px-2 py-1 bg-white"
            placeholder="e.g. Catalyst"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            disabled={busy}
          />
          <div className="mt-2 flex gap-2">
            <button className="btn-primary" disabled={busy || !newName.trim()} onClick={createAndLink}>
              {busy ? "Creating…" : "Create & link"}
            </button>
            <button
              type="button"
              className="text-sm text-slate-600 hover:underline"
              disabled={busy}
              onClick={() => setCreating(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {error && <div className="mt-2 text-xs text-red-600">{error}</div>}
    </div>
  );
}
